/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import * as React from "react";
import styled from "styled-components";
import { AdobeId } from "reducers/CurrentUserReducer";
import selectAvatarForSize from "utilities/ProfileUtilities/selectAvatarForSize";
import { useProfile } from "hooks/useProfile";
import unknownSpeaker from "images/unknown-speaker.svg";
import { getUnknownSpeakerName } from "utilities/getDisplayNameForBlock";

const ParticipantAvatarContainer = styled.div<{
  muted: boolean;
  size: ParticipantAvatarSize;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background-color: var(--spectrum-global-color-static-gray-200);
  border-radius: 50%;
  opacity: ${(props): number => (props.muted ? 0.3 : 1.0)};
  width: ${(props): string => `${avatarSizeInPixels(props.size)}px`};
  height: ${(props): string => `${avatarSizeInPixels(props.size)}px`};
  font-size: ${(props): string => fontSizeCssVariable(props.size)};
  user-select: none;
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
`;

export enum ParticipantAvatarSize {
  SMALL = "SMALL",
  MEDIUM = "MEDIUM",
  LARGE = "LARGE",
}

function fontSizeCssVariable(size: ParticipantAvatarSize): string {
  switch (size) {
    case ParticipantAvatarSize.SMALL:
      return "var(--spectrum-global-dimension-static-font-size-100)";
    case ParticipantAvatarSize.MEDIUM:
      return "var(--spectrum-global-dimension-static-font-size-300)";
    case ParticipantAvatarSize.LARGE:
      return "var(--spectrum-global-dimension-static-font-size-900)";
  }
}

function avatarSizeInPixels(size: ParticipantAvatarSize): number {
  switch (size) {
    case ParticipantAvatarSize.SMALL:
      return 42;
    case ParticipantAvatarSize.MEDIUM:
      return 44;
    case ParticipantAvatarSize.LARGE:
      return 128;
  }
}

const getInitialsForName = (displayName: string): string => {
  const initials = displayName
    .split(" ")
    .map((x) => x[0])
    .join("")
    .toUpperCase();

  const firstLetter = initials[0] || "";
  // Only include the last letter if we have more than one initial
  const lastLetter =
    (initials.length > 1 && initials[initials.length - 1]) || "";

  return `${firstLetter}${lastLetter}`;
};

const ParticipantAvatar: React.FC<{
  adobeId: AdobeId | undefined;
  muted?: boolean;
  size?: ParticipantAvatarSize;
  displayName?: string;
}> = ({
  adobeId,
  muted = false,
  size = ParticipantAvatarSize.SMALL,
  displayName,
}) => {
  const { profile, isLoading } = useProfile(adobeId);
  const defaultedDisplayName = displayName ?? profile?.displayName;
  const unknownSpeakerName = getUnknownSpeakerName();
  if (
    isLoading ||
    defaultedDisplayName === unknownSpeakerName ||
    defaultedDisplayName === undefined
  ) {
    return (
      <AvatarImage
        src={unknownSpeaker}
        alt={I18n.t("recording_sessions.participant_text", {
          name: unknownSpeakerName,
        })}
      />
    );
  }
  const defaultedImages = profile?.images ?? {};

  const image = selectAvatarForSize(defaultedImages, avatarSizeInPixels(size));

  const avatarImage = (
    <AvatarImage
      src={image}
      alt={I18n.t("recording_sessions.participant_text", {
        name: defaultedDisplayName,
      })}
    />
  );

  let content;

  if (
    image?.indexOf("no-image") === -1 &&
    image?.indexOf("/image/default") === -1
  ) {
    content = avatarImage;
  } else {
    content = getInitialsForName(defaultedDisplayName);
  }

  return (
    <ParticipantAvatarContainer muted={muted} size={size}>
      {content}
    </ParticipantAvatarContainer>
  );
};

export default ParticipantAvatar;
